/**
 * The comment thread on a card.
 *
 * ## Typing indicators are sent on transitions, not on keystrokes
 *
 * `actions.setTyping` is called when the composer goes from empty to non-empty and
 * from non-empty to empty, not on every character. The Durable Object debounces
 * again - it broadcasts only when its own `typingCardId` changes - so a frame per
 * keystroke never reaches the network. Sending on transitions rather than on a
 * timer means the indicator is exact: it appears the moment someone starts and
 * disappears the moment they clear the box, with no "typing..." that lingers after
 * they have stopped.
 *
 * ## Editing is author-only, deletion is not
 *
 * The server enforces both: `PATCH` is rejected unless the caller wrote the
 * comment, `DELETE` is allowed for the author or a workspace admin. The UI applies
 * the same rule so the affordances match what will actually succeed - an admin
 * sees a delete button and not an edit button on someone else's comment, which is
 * the honest representation of a deliberate policy: editing another person's words
 * in a shared record is a different act from removing them.
 *
 * ## Mentions
 *
 * `@name` is rendered as a chip by the Markdown renderer. There is no autocomplete
 * and no notification: both need a user directory scoped to the workspace and a
 * delivery mechanism, and neither is what this project is about. The chip exists
 * because a mention that renders as plain text reads like a typo.
 */

import { useRef, useState, type ReactNode } from 'react';
import type { CardComment } from '@slate/shared';
import { useBoard } from '../state/BoardProvider.js';
import { Avatar } from './Avatar.js';
import { Icon, ghostButtonClass, primaryButtonClass, useTick } from './ui.js';
import { relativeTime } from '../lib/format.js';
import { renderMarkdown } from '../lib/markdown.js';

export function CommentThread({ cardId, currentUserId }: { cardId: string; currentUserId: string }): ReactNode {
  const { state, actions } = useBoard();
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const typingRef = useRef(false);

  const now = useTick(true, 30_000);

  // `undefined` means the thread has not been fetched; `[]` means it is empty.
  // Rendering "no comments yet" for the first would be a lie for the half second
  // before the detail request lands.
  const comments = state.comments[cardId];

  function onDraftChange(value: string): void {
    setDraft(value);
    const typing = value.trim() !== '';
    if (typing !== typingRef.current) {
      typingRef.current = typing;
      actions.setTyping(cardId, typing);
    }
  }

  function submit(): void {
    const body = draft.trim();
    if (body === '') return;
    actions.createComment(cardId, body);
    setDraft('');
    typingRef.current = false;
    actions.setTyping(cardId, false);
  }

  return (
    <section aria-label="Comments" className="flex flex-col gap-3">
      {comments === undefined ? (
        <p className="text-xs text-ink-subtle">Loading comments…</p>
      ) : comments.length === 0 ? (
        <p className="text-xs text-ink-subtle">No comments yet.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {comments.map((comment) => (
            <CommentRow
              key={comment.id}
              comment={comment}
              currentUserId={currentUserId}
              now={now}
              editing={editingId === comment.id}
              editDraft={editDraft}
              onEditDraft={setEditDraft}
              onStartEdit={() => {
                setEditingId(comment.id);
                setEditDraft(comment.body);
              }}
              onCancelEdit={() => setEditingId(null)}
              onSaveEdit={() => {
                const body = editDraft.trim();
                if (body !== '' && body !== comment.body) void actions.updateComment(cardId, comment.id, body);
                setEditingId(null);
              }}
              onDelete={() => void actions.deleteComment(cardId, comment.id)}
            />
          ))}
        </ul>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
        className="rounded-lg border border-border bg-canvas p-2"
      >
        <label htmlFor={`comment-body-${cardId}`} className="sr-only">
          Add a comment
        </label>
        <textarea
          id={`comment-body-${cardId}`}
          value={draft}
          rows={3}
          placeholder="Write a comment. Markdown and @mentions are supported."
          onChange={(event) => onDraftChange(event.target.value)}
          onBlur={() => {
            if (typingRef.current) {
              typingRef.current = false;
              actions.setTyping(cardId, false);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              submit();
            }
          }}
          className="w-full resize-y bg-transparent text-sm text-ink placeholder:text-ink-subtle"
        />
        <div className="mt-1.5 flex items-center gap-2">
          <button type="submit" className={primaryButtonClass} disabled={draft.trim() === ''}>
            <Icon name="send" size={14} />
            Comment
          </button>
          <span className="text-[11px] text-ink-subtle">Ctrl+Enter to send</span>
        </div>
      </form>
    </section>
  );
}

function CommentRow({
  comment,
  currentUserId,
  now,
  editing,
  editDraft,
  onEditDraft,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
}: {
  comment: CardComment;
  currentUserId: string;
  now: number;
  editing: boolean;
  editDraft: string;
  onEditDraft(value: string): void;
  onStartEdit(): void;
  onCancelEdit(): void;
  onSaveEdit(): void;
  onDelete(): void;
}): ReactNode {
  const isAuthor = comment.author.id === currentUserId;
  const deleted = comment.deletedAt !== null;

  return (
    <li className="flex gap-2.5">
      <Avatar name={comment.author.name} colour={comment.author.colour} size={26} title={comment.author.name} />
      <div className="min-w-0 flex-1">
        <p className="flex items-baseline gap-2 text-xs">
          <span className="font-medium text-ink">{comment.author.name}</span>
          <time dateTime={comment.createdAt} className="text-ink-subtle">
            {relativeTime(comment.createdAt, now)}
          </time>
          {comment.editedAt !== null && !deleted && <span className="text-ink-subtle">edited</span>}
          {isAuthor && !deleted && !editing && (
            <span className="ml-auto flex gap-1">
              <button
                type="button"
                onClick={onStartEdit}
                className="rounded px-1 text-[11px] text-ink-subtle hover:bg-sunken hover:text-ink"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="rounded px-1 text-[11px] text-ink-subtle hover:bg-danger-soft hover:text-danger"
              >
                Delete
              </button>
            </span>
          )}
        </p>

        {deleted ? (
          // A tombstone rather than a removal. A reply that says "agreed" has to
          // point at something, and the server blanks the body for the same
          // reason - see `mapComment` in `apps/api/src/db/rows.ts`.
          <p className="mt-1 text-sm text-ink-subtle italic">This comment was deleted.</p>
        ) : editing ? (
          <div className="mt-1">
            <label htmlFor={`edit-${comment.id}`} className="sr-only">
              Edit comment
            </label>
            <textarea
              id={`edit-${comment.id}`}
              value={editDraft}
              rows={3}
              autoFocus
              onChange={(event) => onEditDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') onCancelEdit();
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) onSaveEdit();
              }}
              className="w-full resize-y rounded-md border border-border bg-canvas p-2 text-sm text-ink"
            />
            <div className="mt-1.5 flex gap-2">
              <button type="button" onClick={onSaveEdit} className={primaryButtonClass}>
                Save
              </button>
              <button type="button" onClick={onCancelEdit} className={ghostButtonClass}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-1 text-sm text-ink">{renderMarkdown(comment.body, `c-${comment.id}`)}</div>
        )}
      </div>
    </li>
  );
}
